import { users } from '@/mocking/data/mockData'

import type { UserDTO } from '@state/user'

type FakeIdentity = Omit<UserDTO, 'id' | 'storeGrants'>

interface Payload {
  claims: { typ: string; val: string }[]
}

const KEY_LS = 'fake-principal'
const KEY_HEADER = 'X-MS-CLIENT-PRINCIPAL'
let currentFakePrincipal: FakeIdentity | undefined = undefined

// ====================== client side ======================

const ericEmployee = users[1]!

function initialize() {
  try {
    const id = sessionStorage.getItem(KEY_LS)
    if (!id) {
      setFakePrincipal(ericEmployee.id)
      return
    }

    const identity = users.find(user => user.id === id)
    if (identity) {
      currentFakePrincipal = identity
    } else {
      setFakePrincipal(ericEmployee.id)
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (e) { /* empty */ }
}

export function setFakePrincipal(id: string) {

  const identity = users.find(user => user.id === id)

  if (!identity) {
    throw new Error(`No user found with ID: ${id}`)
  }

  currentFakePrincipal = identity
  sessionStorage.setItem(KEY_LS, identity.id)

  return identity
}

function getFakePrincipal() {
  if (!currentFakePrincipal) {
    initialize()
  }

  return currentFakePrincipal
}

export function addFakePrincipalHeader(headers: Headers) {
  const principal = getFakePrincipal()
  if (!principal) return headers

  const payload: Payload = {
    claims: [
      { 'typ': 'preferred_username', 'val': principal.email },
      { 'typ': 'name', 'val': principal.fullname ?? '' },
      { 'typ': 'iss', 'val': 'local' },
      { 'typ': 'role', 'val': principal.role },
    ],
  }

  headers.set(KEY_HEADER, btoa(JSON.stringify(payload)))
  return headers
}

// ====================== mock server side ======================

export function getFakePrincipalFromHeader(headers: Headers): FakeIdentity | undefined {
  const header = headers.get(KEY_HEADER)
  if (!header) return undefined

  try {
    const decoded = atob(header)
    const parsed = JSON.parse(decoded) as Payload
    const email = parsed.claims.find(c => c.typ === 'preferred_username')?.val
    const fullname = parsed.claims.find(c => c.typ === 'name')?.val
    const issuer = parsed.claims.find(c => c.typ === 'iss')?.val
    const role = parsed.claims.find(c => c.typ === 'role')?.val

    if (!email || !fullname || !issuer || !role) return undefined

    return { email, fullname, role } as FakeIdentity
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (e) {
    return undefined
  }
}